import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const WIDGET_OPTIONS = ['none', 'select_id', 'ciiu_level', 'problem'];

const isTruthy = (value) => value === true || value === 'true' || value === 1 || value === '1';

const SearchFilterFieldCard = ({
  row,
  surfaceId,
  busy,
  expanded,
  included,
  otherIds,
  blockedReason,
  onToggleSurface,
  onPatch,
  onToggleExpand,
}) => {
  const { t } = useTranslation();
  const label = t(`semanticSearch.filterFields.${row.field_name}`, { defaultValue: row.field_name });
  const dimmed = !isTruthy(row.activated) || isTruthy(row.deleted);
  const showWidget = surfaceId === 'panel_l3' || surfaceId === 'bar_combo' || surfaceId === 'all' || expanded;
  const showCiiu = row.field_name === 'catalogciiu_levels' || row.combo_widget === 'ciiu_level';

  return (
    <div className={`admin-search-filters__card${dimmed ? ' admin-search-filters__dim' : ''}`}>
      <div className="admin-search-filters__card-top">
        <div>
          <div className="admin-search-filters__card-title">{label}</div>
          <code className="admin-search-filters__card-code">{row.field_name}</code>
          {row.source_kind && (
            <div className="small text-muted">{row.source_kind}{row.relation_as ? ` · ${row.relation_as}` : ''}</div>
          )}
        </div>
        {surfaceId !== 'all' && (
          <Form.Check
            type="switch"
            id={`surface-${surfaceId}-${row.id}`}
            label={included ? t('semanticSearch.adminFilters.formOn') : t('semanticSearch.adminFilters.formOff')}
            checked={included}
            disabled={busy || (included && Boolean(blockedReason))}
            onChange={() => onToggleSurface(row, !included)}
          />
        )}
      </div>

      {otherIds.length > 0 && (
        <div className="small text-muted mt-1">
          {t('semanticSearch.adminFilters.formAlsoIn', {
            list: otherIds.map((id) => t(`semanticSearch.adminFilters.surface.${id}`)).join(', '),
          })}
        </div>
      )}
      {included && blockedReason && (
        <div className="small text-warning mt-1">{t(`semanticSearch.adminFilters.formLocked.${blockedReason}`)}</div>
      )}

      <div className="admin-search-filters__card-controls">
        <Form.Group>
          <Form.Label>{t('semanticSearch.adminFilters.formPriority')}</Form.Label>
          <Form.Control
            key={`${row.id}-priority-${row.priority}`}
            type="number"
            size="sm"
            defaultValue={row.priority ?? 0}
            disabled={busy}
            onBlur={(event) => {
              const next = Number(event.target.value);
              if (!Number.isFinite(next) || next === Number(row.priority)) return;
              onPatch(row, { priority: next });
            }}
          />
        </Form.Group>
        {showWidget && (
          <Form.Group>
            <Form.Label>{t('semanticSearch.adminFilters.colWidget')}</Form.Label>
            <Form.Select
              size="sm"
              value={row.combo_widget || 'none'}
              disabled={busy}
              onChange={(event) => onPatch(row, { combo_widget: event.target.value })}
            >
              {WIDGET_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        {showCiiu && (
          <Form.Group>
            <Form.Label>{t('semanticSearch.adminFilters.formCiiuLevel')}</Form.Label>
            <Form.Select
              size="sm"
              value={row.ciiu_level || 2}
              disabled={busy}
              onChange={(event) => onPatch(row, { ciiu_level: Number(event.target.value) })}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </Form.Select>
          </Form.Group>
        )}
      </div>

      <Button variant="link" size="sm" className="px-0" onClick={() => onToggleExpand(row.id)}>
        {expanded ? t('semanticSearch.adminFilters.formHideDetail') : t('semanticSearch.adminFilters.formShowDetail')}
      </Button>

      {expanded && (
        <div className="mt-2">
          <div className="small fw-semibold mb-2">{t('semanticSearch.adminFilters.formDetailTitle')}</div>
          <div className="admin-search-filters__detail-grid">
            {['activated', 'use_in_like', 'use_in_combo', 'use_in_panel', 'use_in_bridge', 'use_in_m4', 'deleted'].map((key) => (
              <Form.Check
                key={key}
                type="switch"
                id={`detail-${key}-${row.id}`}
                label={t(`semanticSearch.adminFilters.formFlag.${key}`)}
                checked={isTruthy(row[key])}
                disabled={busy}
                onChange={() => onPatch(row, {
                  [key]: !isTruthy(row[key]),
                  ...(key === 'deleted' && !isTruthy(row.deleted) ? { activated: false } : {}),
                })}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilterFieldCard;
